import type { RagAssistantMessage, RagImageItem, RagVideoItem } from "../types/ragChatTypes";
import type { RagChatQueryResponse } from "../services/ragChatApi";
import { resolveMediaPath } from "../services/ragChatApi";
import { ragId } from "../data/mockRagData";
import { videoDisplayName } from "./ragChatUtils";

function mapImages(
  items: Array<{ path: string; metadata?: { page?: string | number } }> | undefined
): RagImageItem[] {
  if (!items?.length) return [];
  return items.map((item) => ({
    path: resolveMediaPath(item.path ?? ""),
    metadata: item.metadata,
  }));
}

function mapInlineImages(
  inline: RagChatQueryResponse["inlineImages"] | undefined
): Record<string, RagImageItem[]> {
  if (!inline) return {};
  const out: Record<string, RagImageItem[]> = {};
  for (const [page, list] of Object.entries(inline)) {
    out[page] = mapImages(list);
  }
  return out;
}

export function mapQueryResponseToAssistantMessage(
  data: RagChatQueryResponse
): RagAssistantMessage {
  return {
    id: ragId("assistant"),
    role: "assistant",
    answer: data.answer ?? "No answer returned.",
    images: mapImages(data.images),
    videos: (data.videos ?? []).map((v): RagVideoItem => {
      const rawPath = v.path ?? "";
      const fromMeta = String(v.metadata?.pdf_name ?? "").trim();
      return {
        path: resolveMediaPath(rawPath),
        name: fromMeta || videoDisplayName(rawPath),
      };
    }),
    sources: data.sources ?? [],
    options: data.options ?? [],
    inlineImages: mapInlineImages(data.inlineImages),
    pageTables: data.pageTables ?? {},
    pageAlerts: data.pageAlerts ?? {},
    relatedQuestions: data.relatedQuestions ?? [],
    alternativePdfs: data.alternativePdfs ?? [],
    responseType: data.responseType ?? "answer",
    queryType: null,
    primarySource: data.primarySource ?? null,
    alternativeSources: data.alternativeSources ?? [],
    aiEnhancement: data.aiEnhancement ?? null,
    refusalReason: data.refusalReason ?? null,
  };
}

export function createChatErrorAssistantMessage(message: string): RagAssistantMessage {
  return {
    id: ragId("assistant"),
    role: "assistant",
    answer: message,
    images: [],
    videos: [],
    sources: [],
    options: [],
    inlineImages: {},
    pageTables: {},
    pageAlerts: {},
    relatedQuestions: [],
    alternativePdfs: [],
    responseType: "answer",
    queryType: null,
    primarySource: null,
    alternativeSources: [],
    aiEnhancement: null,
  };
}
